import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Lock, Mail, AlertCircle } from 'lucide-react';
import { authService } from '../authService';
import { AUTH_CONFIG } from '../config';

// Verification Boxes Component with Animation
const VerificationBoxes = ({ onComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [digitsLeft, setDigitsLeft] = useState(6);

  useEffect(() => {
    // Calculate digits left
    const filledDigits = code.filter(digit => digit !== '').length;
    setDigitsLeft(6 - filledDigits);
    
    // Call onComplete when all digits are filled
    if (filledDigits === 6) {
      onComplete(code.join(''));
    }
  }, [code, onComplete]);

  // Handle input change
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    // Create a new array with the updated value
    const newCode = [...code];
    newCode[index] = value.slice(0, 1); // Only take first character
    setCode(newCode);
    
    // Auto-focus next input if a digit was entered
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle keydown
  const handleKeyDown = (index, e) => {
    // Move to previous input when backspace is pressed on an empty input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is all digits
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, 6);
      const newCode = [...code];
      
      digits.forEach((digit, index) => {
        if (index < 6) newCode[index] = digit;
      });
      
      setCode(newCode);
      
      // Focus the appropriate input after paste
      if (digits.length < 6) {
        inputRefs.current[digits.length].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <div className="flex space-x-2 md:space-x-4 justify-center mb-6">
        {code.map((digit, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.08 }}
            className={`w-12 h-16 rounded-lg flex items-center justify-center ${digit ? 'bg-green border-2 border-green shadow-md' : ' border-2 border-green'}`}
          >
            <input
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              className="w-full h-full text-center text-2xl bg-transparent border-none outline-none"
            />
          </motion.div>
        ))}
      </div>
      
      <motion.div 
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="bg-beige-light p-3 rounded-lg text-center mt-4"
      >
        <p className="text-gray-600 text-lg">{digitsLeft} numeri mancanti</p>
      </motion.div>
    </motion.div>
  );
};

// Message Component with Animation
const Message = ({ message, type = 'info' }) => {
  const bgColor = type === 'error' ? 'bg-red-50' : 
                 type === 'success' ? 'bg-green-50' : 'bg-blue-50';
  const textColor = type === 'error' ? 'text-red-700' : 
                   type === 'success' ? 'text-green-700' : 'text-blue-700';
  const Icon = type === 'error' ? AlertCircle : 
              type === 'success' ? CheckCircle : Mail;

  return message ? (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className={`p-4 mb-4 rounded-lg flex items-start ${bgColor}`}
    >
      <Icon className={`mr-2 ${textColor}`} size={20} />
      <p className={textColor}>{message}</p>
    </motion.div>
  ) : null;
};

// PurchaseFormContent with animations
const PurchaseFormContent = ({ productId }) => {
  const { user, signIn, refreshAuthState } = useAuth();
  
  // Initialize state from localStorage if available
  const [email, setEmail] = useState(() => {
    const savedEmail = localStorage.getItem('purchase_email');
    return savedEmail || '';
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [otpCode, setOtpCode] = useState('');
  
  // Initialize form visibility states from localStorage if available
  const [showVerificationForm, setShowVerificationForm] = useState(() => {
    return localStorage.getItem('purchase_show_verification_form') === 'true';
  });
  
  const [showOtpForm, setShowOtpForm] = useState(() => {
    return localStorage.getItem('purchase_show_otp_form') === 'true';
  });
  
  const [showLoginForm, setShowLoginForm] = useState(() => {
    return localStorage.getItem('purchase_show_login_form') === 'true';
  });
  
  const [showPasswordSetupForm, setShowPasswordSetupForm] = useState(() => {
    return localStorage.getItem('purchase_show_password_setup_form') === 'true';
  });
  
  const [requiresPasswordSetup, setRequiresPasswordSetup] = useState(() => {
    return localStorage.getItem('purchase_requires_password_setup') === 'true';
  });
  
  const [loading, setLoading] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(() => {
    return localStorage.getItem('purchase_complete') === 'true';
  });

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('purchase_email', email);
  }, [email]);

  useEffect(() => {
    localStorage.setItem('purchase_show_verification_form', showVerificationForm);
  }, [showVerificationForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_otp_form', showOtpForm);
  }, [showOtpForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_login_form', showLoginForm);
  }, [showLoginForm]);

  useEffect(() => {
    localStorage.setItem('purchase_show_password_setup_form', showPasswordSetupForm);
  }, [showPasswordSetupForm]);

  useEffect(() => {
    localStorage.setItem('purchase_requires_password_setup', requiresPasswordSetup);
  }, [requiresPasswordSetup]);

  useEffect(() => {
    localStorage.setItem('purchase_complete', purchaseComplete);
  }, [purchaseComplete]);
  
  // Updated effect to check for password setup requirement when user is logged in
  useEffect(() => {
    if (user) {
      // Only update state if we're not in the password setup flow
      if (!requiresPasswordSetup && !showPasswordSetupForm) {
        // Check if the user needs password setup
        const checkPasswordStatus = async () => {
          try {
            const token = authService.getToken();
            if (!token) return;
            
            // Fetch user profile to check password status
            const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/user`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userData = await response.json();
              console.log("responsedata = ", userData)
              // If user has a profile and has_password is false, they need to set a password
              if (userData && userData.data && userData.data.profile && userData.data.profile.has_password === false) {
                console.log("User needs to set a password");
                setRequiresPasswordSetup(true);
                setShowPasswordSetupForm(true);
                setShowVerificationForm(false);
                setShowOtpForm(false);
                setShowLoginForm(false);
                setMessage('Per favore, imposta una password per il tuo account');
                setMessageType('info');
                return;
              }
            }
            
            // If we get here, user is logged in and doesn't need password setup
            if (showVerificationForm || showOtpForm || showLoginForm) {
              setShowVerificationForm(false);
              setShowOtpForm(false);
              setShowLoginForm(false);
              setMessage('Hai già effettuato l\'accesso, clicca acquista per procedere!');
              setMessageType('success');
            }
          } catch (error) {
            console.error("Error checking password status:", error);
          }
        };
        
        checkPasswordStatus();
      }
    }
  }, [user, showVerificationForm, showOtpForm, showLoginForm, showPasswordSetupForm, requiresPasswordSetup]);

  // Clear purchase state
  const clearPurchaseState = () => {
    localStorage.removeItem('purchase_email');
    localStorage.removeItem('purchase_show_verification_form');
    localStorage.removeItem('purchase_show_otp_form');
    localStorage.removeItem('purchase_show_login_form');
    localStorage.removeItem('purchase_show_password_setup_form');
    localStorage.removeItem('purchase_requires_password_setup');
    localStorage.removeItem('purchase_complete');
    
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setMessage('');
    setOtpCode('');
    setShowVerificationForm(false);
    setShowOtpForm(false);
    setShowLoginForm(false);
    setShowPasswordSetupForm(false);
    setRequiresPasswordSetup(false);
    setPurchaseComplete(false);
  };

  // Funzione per inviare il codice OTP
  const sendOtp = async (email) => {
    console.log("Invio codice OTP a:", email);
    
    try {
      // Validazione formato email
      if (!email || !email.includes('@') || !email.includes('.')) {
        return {
          error: {
            message: "Formato email non valido"
          }
        };
      }
      
      // Chiamata all'API per l'OTP
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Errore API OTP:", errorData);
        return {
          error: {
            message: errorData.detail || "Impossibile inviare il codice di verifica"
          }
        };
      }
      
      const data = await response.json();
      return { data };
      
    } catch (error) {
      console.error("Eccezione durante l'invio dell'OTP:", error);
      return {
        error: {
          message: error.message || "Impossibile inviare il codice di verifica"
        }
      };
    }
  };

  // Updated function to verify OTP
  const verifyOtp = async (email, otpCode) => {
    try {
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(),
          otp_code: otpCode,
          password_bind: true // Add the password_bind flag
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Verifica email fallita' };
      }
      
      const tokenData = await response.json();
      
      // Validazione dati token
      if (!tokenData || !tokenData.access_token) {
        return { error: 'Dati token non validi' };
      }
      
      // Check if password registration is necessary from the response
      const necessaryPasswordRegistration = tokenData.necessary_password_registration || false;
      console.log("API indicates password registration needed:", necessaryPasswordRegistration);
      
      // IMPORTANT: For password setup flow, we need to handle auth state update carefully
      if (necessaryPasswordRegistration) {
        // Just save tokens without refreshing auth state yet
        authService.saveTokens(tokenData);
        // Don't call refreshAuthState() here to maintain control of UI flow
      } else {
        // Regular flow - save tokens and update auth state
        authService.saveTokens(tokenData);
        await refreshAuthState();
      }
      
      return { 
        data: tokenData,
        necessaryPasswordRegistration 
      };
    } catch (error) {
      console.error('Errore verifica email:', error);
      return { error: 'Verifica fallita' };
    }
  };

  // Controlla se l'email esiste con password
  const checkEmailExists = async (email) => {
    try {
      const token = authService.getToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/email-exist?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { error: errorData.detail || 'Impossibile verificare email' };
      }
      

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('Errore verifica email:', error);
      return { error: 'Impossibile verificare se l\'email esiste' };
    }
  };

  // Elabora l'acquisto tramite API
  const processPurchase = async (userEmail) => {
    setLoading(true);
    try {
      // Ottieni l'URL del sito web corrente
      const websiteRootUrl = window.location.origin;
      
      // Crea gli URL di successo e annullamento
      const successUrl = `${websiteRootUrl}/contenuti`;
      const cancelUrl = window.location.href;
      
      const token = authService.getToken();
      const response = await fetch(`${AUTH_CONFIG.apiUrl}/simple-purchase-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userEmail,
          productId: productId,
          success_url: successUrl,
          cancel_url: cancelUrl
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Errore durante l\'accesso al prodotto');
      }
      
      // Verifica se il prodotto è già stato acquistato
      if (data.purchase_status === "already_purchased") {
        setMessage(data.message || 'Hai già acquistato questo prodotto!');
        setMessageType('info');
        
        // Reindirizza alla pagina dei contenuti dopo un breve ritardo
        setTimeout(() => {
          window.location.href = '/contenuti';
        }, 1500);
        
        return true;
      }
      
      // Se l'URL di checkout è fornito nella risposta, reindirizza ad esso
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return true;
      }
      
      setMessage("L'acquisto è andato a buon fine! Grazie per il supporto!");
      setMessageType('success');
      setPurchaseComplete(true);
      // Cancella lo stato memorizzato all'acquisto riuscito
      clearPurchaseState();
      return true;
    } catch (error) {
      console.error('Errore durante l\'acquisto:', error);
      setMessage(`Acquisto fallito: ${error.message}`);
      setMessageType('error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Gestione clic pulsante acquisto
  const handlePurchase = () => {
    if (user) {
      // Check if user needs password setup before purchase
      if (requiresPasswordSetup) {
        setShowPasswordSetupForm(true);
        setMessage('Per favore, imposta una password per il tuo account prima di procedere con l\'acquisto');
        setMessageType('info');
      } else {
        // Utente già loggato e con password, procedi direttamente con l'acquisto
        processPurchase(user.email);
      }
    } else {
      // Utente non loggato, mostra form email
      setShowVerificationForm(true);
    }
  };

  // Gestione flusso "Password dimenticata"
  const handleForgotPassword = async () => {
    if (!email) {
      setMessage('Inserisci il tuo indirizzo email');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    
    try {
      // Richiedi reset password tramite OTP
      const { error } = await sendOtp(email);
      
      if (error) {
        setMessage(`Errore: ${error.message}`);
        setMessageType('error');
      } else {
        setMessage('Controlla il codice di verifica che ti abbiamo mandato via email!');
        setMessageType('info');
        setShowLoginForm(false);
        setShowOtpForm(true);
        setRequiresPasswordSetup(true); // Necessaria l'impostazione di una nuova password
      }
    } catch (error) {
      console.error('Errore in handleForgotPassword:', error);
      setMessage(`Si è verificato un errore: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Funzione migliorata per verificare l'email
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setMessage('Ti preghiamo di inserire un indirizzo email valido!');
      setMessageType('error');
      return;
    }
   
    setLoading(true);
    
    try {
      // FASE 1: Verifica se l'utente esiste e ha una password
      const { data, error } = await checkEmailExists(email);
      
      if (error) {
        console.error("Errore verifica email:", error);
        setMessage(`Errore durante la verifica dell'email: ${error}`);
        setMessageType('error');
        setLoading(false);
        return;
      }
      
      const userExistsInDb = data?.exist || false;
      const userHasPassword = data?.has_password || false;
      
      console.log("Stato utente:", { userExistsInDb, userHasPassword });
      
      // Se l'utente esiste e ha una password, mostra il form di login
      if (userExistsInDb && userHasPassword) {
        setMessage('Inserisci la tua password per continuare!');
        setMessageType('info');
        setShowLoginForm(true);
        setShowOtpForm(false);
        setLoading(false);
        return;
      }
      
      // Altrimenti, invia OTP per la verifica
      const otpResult = await sendOtp(email);
      
      if (otpResult.error) {
        setMessage(`Errore: ${otpResult.error.message}`);
        setMessageType('error');
      } else {
        // Imposta requisito setup password in base all'esistenza dell'utente
        // Se l'utente esiste ma non ha password, imposta flag per setup password
        setRequiresPasswordSetup(!userHasPassword);
        setMessage('Controlla il codice di verifica che ti abbiamo mandato via email!');
        setMessageType('info');
        setShowOtpForm(true);
      }
      
    } catch (error) {
      console.error('Errore in handleVerifyEmail:', error);
      setMessage(`Si è verificato un errore: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };
  
  // Aggiunge un pulsante di reset per consentire agli utenti di ricominciare da capo
  const renderResetButton = () => {
    if (showVerificationForm || showOtpForm || showLoginForm || showPasswordSetupForm) {
      return (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          type="button"
          onClick={clearPurchaseState}
          className="w-full text-gray-500 text-sm mt-6 hover:text-gray-700 transition-colors"
        >
          Ricomincia da capo
        </motion.button>
      );
    }
    return null;
  };
  
  // Varianti di animazione pulsante
  const buttonVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    tap: { scale: 0.98 },
    hover: { scale: 1.02 }
  };
  
  // Varianti di animazione form
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
  };
  
  // Renderizza il form appropriato in base allo stato
  const renderForm = () => {
    if (purchaseComplete) {
      return (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          className="p-6 text-center bg-green-50 rounded-lg shadow-md"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="flex justify-center mb-4"
          >
            <CheckCircle size={60} className="text-green-500" />
          </motion.div>
          <h3 className="text-xl font-bold text-green-700 mb-2">Acquisto Completato!</h3>
          <p className="text-green-600">Il tuo acquisto è stato completato con successo.</p>
        </motion.div>
      );
    }
    
    if (!showVerificationForm) {
      return (
        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          onClick={handlePurchase}
          disabled={loading}
          className="w-full py-4 px-6 bg-accent text-white rounded-full font-bold text-lg shadow-md hover:bg-accent transition-colors duration-300"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Caricamento...
            </div>
          ) : 'Acquista'}
        </motion.button>
      );
    }
    
    if (showPasswordSetupForm) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={async (e) => {
            e.preventDefault();
            
            if (newPassword !== confirmPassword) {
              setMessage('Le password non corrispondono');
              setMessageType('error');
              return;
            }
            
            if (newPassword.length < 8) {
              setMessage('La password deve essere di almeno 8 caratteri');
              setMessageType('error');
              return;
            }
            
            setLoading(true);
            
            try {
              // Aggiorna utente con nuova password
              const token = authService.getToken();
              if (!token) {
                setMessage('Sessione scaduta. Per favore ricomincia.');
                setMessageType('error');
                setLoading(false);
                return;
              }
              console.log("AAAAAAAAA")
              // Aggiorna utente tramite API
              const response = await fetch(`${AUTH_CONFIG.apiUrl}/auth/user`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  email:email,
                  password: newPassword,
                  has_password: true
                })
                
              });
              console.log("AAAAAAAAA", response)

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Errore impostazione password');
              }
              
              // Get response data
              const updateResponse = await response.json();
              console.log("Password update response:", updateResponse);
              
              // Clear password setup flag
              setRequiresPasswordSetup(false);
              localStorage.removeItem('purchase_requires_password_setup');
              
              // Aggiorna stato autenticazione
              await refreshAuthState();
              
              setMessage('Password impostata con successo!');
              setMessageType('success');
              
              // Nascondi il form password e prosegui con l'acquisto
              setShowPasswordSetupForm(false);
              
              // Dopo un breve ritardo, elabora l'acquisto
              setTimeout(async () => {
                await processPurchase(email);
              }, 1000);
            } catch (error) {
              setMessage(`Errore nell'impostazione della password: ${error.message}`);
              setMessageType('error');
            } finally {
              setLoading(false);
            }
          }} 
          className="space-y-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl font-bold text-green mb-6 text-center">Crea la tua password</h2>
            <p className="text-gray-600 mb-6 text-center">Scegli una password sicura per confermare la creazione del tuo account</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-4"
          >
            <label 
              htmlFor="newPassword" 
              className="block mb-2 font-medium text-gray-700"
            >
              Nuova Password:
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
                placeholder="Minimo 8 caratteri"
              />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <label 
              htmlFor="confirmPassword" 
              className="block mb-2 font-medium text-gray-700"
            >
              Conferma Password:
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
                placeholder="Conferma la tua password"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <div className="flex items-start">
              <div className="flex items-center h-5 mt-1">
                <input
                  id="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-green-300"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="mb-2 font-medium text-gray-700">
                  Accetto i <a href="/terms" target="_blank" className="text-green hover:underline">Termini di Servizio</a> e la <a href="/privacy" target="_blank" className="text-green hover:underline">Privacy Policy</a>
                </label>
              </div>
            </div>
          </motion.div>

          <motion.button
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileTap="tap"
            whileHover="hover"
            disabled={loading || !newPassword || !confirmPassword || !termsAccepted}
            type="submit"
            className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                Impostando password...
              </div>
            ) : "Conferma la password e continua con l'acquisto"}
          </motion.button>
          
          {renderResetButton()}
        </motion.form>
      );
    }
    
    if (showLoginForm) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={async (e) => {
            e.preventDefault();
            setLoading(true);
            
            try {
              // Tenta il login con email e password
              const { error } = await signIn(email, password);
              
              if (error) {
                setMessage(`Errore durante il login: ${error}`);
                setMessageType('error');
              } else {
                setMessage('Login effettuato con successo!');
                setMessageType('success');
                
                // Elabora l'acquisto dopo login riuscito
                await processPurchase(email);
              }
            } catch (error) {
              setMessage(`Errore durante il login: ${error.message}`);
              setMessageType('error');
            } finally {
              setLoading(false);
            }
          }} 
          className="space-y-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl font-bold text-green mb-6 text-center">Ti diamo il Benvenuto!</h2>
            <p className="text-gray-600 mb-6 text-center">Esegui l'accesso per completare l'acquisto</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-4"
          >
            <label
              htmlFor="email" 
              className="block mb-2 font-medium text-gray-700"
            >
              Email:
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="email"
                type="email"
                value={email}
                readOnly
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg bg-gray-50 text-gray-600"
              />
            </div>
          </motion.div>
  
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-6"
          >
            <label 
              htmlFor="password" 
              className="block mb-2 font-medium text-gray-700"
            >
              Password:
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
                placeholder="Inserisci la tua password"
              />
            </div>
          </motion.div>
  
          <motion.button
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileTap="tap"
            whileHover="hover"
            disabled={loading || !password}
            type="submit"
            className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                Login in corso...
              </div>
            ) : 'Accedi'}
          </motion.button>
  
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            type="button"
            onClick={handleForgotPassword}
            disabled={loading}
            className="w-full mt-4 py-3 px-6 bg-transparent text-green border-2 border-green rounded-full font-medium hover:bg-green-50 transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? 'Elaborazione...' : 'Password dimenticata?'}
          </motion.button>
  
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            type="button"
            onClick={() => {
              setShowLoginForm(false);
              setShowVerificationForm(false);
            }}
            className="w-full text-green text-sm mt-4 hover:text-accent transition-colors"
          >
            Torna all'acquisto
          </motion.button>
  
          {renderResetButton()}
        </motion.form>
      );
    }
  
    if (showOtpForm) {
      return (
        <motion.form 
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={formVariants}
          onSubmit={async (e) => {
            e.preventDefault();
            setLoading(true);
  
            try {
              // Chiama la funzione verifyOtp aggiornata
              const { data, error, necessaryPasswordRegistration } = await verifyOtp(email, otpCode);
  
              if (error) {
                setMessage(`Verifica fallita: ${error}`);
                setMessageType('error');
              } else {
                // Controlla se è necessaria l'impostazione della password
                // Questo può venire sia dal flag necessaryPasswordRegistration della risposta
                // che dal nostro stato requiresPasswordSetup preesistente
                if (necessaryPasswordRegistration || requiresPasswordSetup) {
                  // Se email verificata richiede setup password
                  setMessage('Email verificata, scegli la tua nuova password!');
                  setMessageType('success');
                  setShowOtpForm(false);
                  setShowPasswordSetupForm(true);
                  // Aggiorna lo stato in modo che sia coerente con la risposta dell'API
                  setRequiresPasswordSetup(true);
                  localStorage.setItem('purchase_requires_password_setup', 'true');
                } else {
                  // Flusso di verifica regolare
                  setMessage('Email verificata con successo!');
                  setMessageType('success');
                  
                  // Se necessario, aggiorna stato auth (in caso non sia stato aggiornato in verifyOtp)
                  if (necessaryPasswordRegistration) {
                    await refreshAuthState();
                  }
                  
                  // Nascondi form OTP
                  setShowOtpForm(false);
                  
                  // Elabora l'acquisto dopo verifica
                  await processPurchase(email);
                }
              }
            } catch (error) {
              setMessage(`Errore durante la verifica: ${error.message}`);
              setMessageType('error');
            } finally {
              setLoading(false);
            }
          }} 
          className="flex flex-col items-center w-full"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center mb-6 w-full"
          >
            <h2 className="text-2xl font-bold text-green mb-2">Conferma la tua email</h2>
            <p className="text-gray-600">Inserisci il codice di 6 cifre inviato alla email {email}</p>
          </motion.div>
  
          <VerificationBoxes 
            onComplete={(code) => setOtpCode(code)}
          />
  
          <motion.button
            variants={buttonVariants}
            initial="initial"
            animate="animate"
            whileTap="tap"
            whileHover="hover"
            disabled={loading || otpCode.length !== 6}
            type="submit"
            className="w-full py-3 px-6 mt-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                Verifica in corso...
              </div>
            ) : 'Conferma il codice'}
          </motion.button>
  
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            type="button"
            onClick={() => setShowOtpForm(false)}
            className="text-green text-sm mt-4 hover:text-accent transition-colors"
          >
            Torna indietro
          </motion.button>
  
          {renderResetButton()}
        </motion.form>
      );
    }
  
    // Form email (predefinito se non mostra altri form)
    return (
      <motion.form 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={formVariants}
        onSubmit={handleVerifyEmail} 
        className="space-y-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-2xl font-bold text-green mb-6 text-center">Completa l'Acquisto</h2>
          <p className="text-gray-600 mb-6 text-center">Inserisci la tua email per continuare.</p>
        </motion.div>
  
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <label 
            htmlFor="email" 
            className="block mb-2 font-medium text-gray-700"
          >
            Email:
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full p-3 pl-10 border-2 border-gray-200 rounded-lg focus:border-green focus:ring-0 transition-colors outline-none"
              placeholder="Inserisci il tuo indirizzo email"
            />
          </div>
        </motion.div>
  
        <motion.button
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileTap="tap"
          whileHover="hover"
          disabled={loading}
          type="submit"
          className="w-full py-3 px-6 bg-green text-white rounded-full font-bold shadow-md hover:bg-accent transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
              Elaborazione...
            </div>
          ) : 'Verifica la tua email'}
        </motion.button>
  
        {renderResetButton()}
      </motion.form>
    );
  };
  
  return (
    <div className="purchase-form">
      <AnimatePresence>
        {message && (
          <Message message={message} type={messageType} />
        )}
      </AnimatePresence>
  
      <AnimatePresence mode="wait">
        {renderForm()}
      </AnimatePresence>
  
      {/* Sezione metodi di pagamento con Framer Motion */}
      <div className="mt-6">
        <div className="grid grid-cols-6 gap-1 items-center">
          <div className="flex items-center justify-center">
            <i className="fab fa-cc-visa text-2xl text-blue-600"></i>
          </div>
          <div className="flex items-center justify-center">
            <i className="fab fa-cc-mastercard text-2xl text-red-500"></i>
          </div>
          <div className="flex items-center justify-center">
            <i className="fab fa-amazon-pay text-2xl text-blue-800"></i>
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/images/payment-methods/satispay.webp" 
              alt="Satispay" 
              className="w-full h-auto max-h-8 object-contain" 
            />
          </div>
          <div className="flex items-center justify-center">
            <i className="fab fa-apple-pay text-2xl text-gray-900"></i>
          </div>
          <div className="flex items-center justify-center">
            <i className="fab fa-google-pay text-2xl text-gray-700"></i>
          </div>
        </div>
      </div>
    </div>
  );
  };
  export default PurchaseFormContent;
